<script setup lang="ts">
// The "Suit up" panel: people style their worker avatar like an emoji. Changes apply as they're picked. The panel is
// drawn for the dark navy Toolkit landing page; position it with CSS.
import { nextTick, onMounted, ref, useTemplateRef } from 'vue';
import { useEventListener } from '@vueuse/core';
import WorkerAvatarFace from './WorkerAvatarFace.vue';
import ColorPicker from '../color-picker/ColorPicker.vue';
import {
  WORKER_AVATAR_LOOK_DEFAULT,
  workerAvatarColorKeys,
  workerAvatarSwatches,
  workerAvatarWearsCompany,
  type WorkerAvatarColorKey,
  type WorkerAvatarCompany,
} from './worker-avatar';
import type { WorkerAvatarHairLength, WorkerAvatarLook } from '../../cross-app-shared/types/worker-avatar';

const look = defineModel<WorkerAvatarLook>('look', { required: true });

defineProps<{
  // Offers the company's colours, or null for Toolkit's alone.
  company: WorkerAvatarCompany | null;
}>();

const emit = defineEmits<{
  close: [];
}>();

const hairLengths: { value: WorkerAvatarHairLength; label: string }[] = [
  { value: 'short', label: 'Short' },
  { value: 'long', label: 'Long' },
];
const colorLabels: Record<WorkerAvatarColorKey, string> = {
  skin: 'Skin',
  hair: 'Hair',
  hat: 'Hard hat',
  suit: 'Suit',
  stripe: 'Stripes',
};

function companyWear(company: WorkerAvatarCompany) {
  look.value = { ...look.value, hat: company.wear.hat, stripe: company.wear.stripe };
}

// The panel itself takes focus, so no option looks picked by a focus ring.
const panelEl = useTemplateRef<HTMLElement>('panel');
onMounted(() => panelEl.value?.focus({ preventScroll: true }));

// The last swatch in each row opens a colour picker below it, for any colour at all. Clicking anywhere else closes it,
// and Escape closes it before it closes the panel.
const pickerKey = ref<WorkerAvatarColorKey | null>(null);
const picker = ref<InstanceType<typeof ColorPicker> | null>(null);
async function pickerToggle(key: WorkerAvatarColorKey) {
  pickerKey.value = pickerKey.value === key ? null : key;
  await nextTick();
  picker.value?.focus();
}
function pickerClose() {
  const key = pickerKey.value;
  pickerKey.value = null;
  panelEl.value?.querySelector<HTMLElement>(`[data-picker-trigger="${key}"]`)?.focus();
}
useEventListener(document, 'pointerdown', (event: PointerEvent) => {
  if (pickerKey.value && !(event.target as Element).closest('.wa-picker, .wa-custom')) pickerKey.value = null;
});
useEventListener(document, 'keydown', (event: KeyboardEvent) => {
  if (event.key !== 'Escape') return;
  if (pickerKey.value) pickerClose();
  else emit('close');
});
</script>

<template>
  <section ref="panel" class="wa-customizer" aria-label="Mission specialist" tabindex="-1">
    <div class="wa-head">
      <WorkerAvatarFace class="wa-face" :look />
      <div class="wa-eyebrow">Mission specialist</div>
      <button class="wa-close" type="button" aria-label="Close" @click="emit('close')">&times;</button>
    </div>
    <div class="wa-row">
      <span class="wa-label">Hair length</span>
      <div class="wa-segments">
        <button
          v-for="hairLength in hairLengths"
          :key="hairLength.value"
          type="button"
          :aria-pressed="look.hairLength === hairLength.value"
          @click="look = { ...look, hairLength: hairLength.value }"
        >
          {{ hairLength.label }}
        </button>
      </div>
    </div>
    <div v-if="company" class="wa-row">
      <span class="wa-label">Company</span>
      <button
        class="wa-wear"
        type="button"
        :aria-pressed="workerAvatarWearsCompany(look, company)"
        @click="companyWear(company)"
      >
        <i><b :style="{ background: company.wear.hat }"></b><b :style="{ background: company.wear.stripe }"></b></i>Wear
        {{ company.name }} colours
      </button>
    </div>
    <div v-for="key in workerAvatarColorKeys" :key class="wa-row">
      <span class="wa-label">{{ colorLabels[key] }}</span>
      <div class="wa-swatches">
        <template v-for="(swatch, i) in workerAvatarSwatches(key, company, look)" :key="i">
          <span v-if="swatch.kind === 'divider'" class="wa-divider"></span>
          <button
            v-else-if="swatch.kind === 'custom'"
            class="wa-custom"
            type="button"
            aria-label="Any colour"
            title="Any colour"
            aria-haspopup="dialog"
            :aria-expanded="pickerKey === key"
            :aria-pressed="swatch.color !== null"
            :data-picker-trigger="key"
            @click="pickerToggle(key)"
          >
            <span v-if="swatch.color" :style="{ background: swatch.color }"></span>
          </button>
          <button
            v-else
            type="button"
            :aria-label="swatch.label"
            :title="swatch.label"
            :aria-pressed="look[key] === swatch.color"
            :style="{ background: swatch.color }"
            @click="look = { ...look, [key]: swatch.color }"
          ></button>
        </template>
      </div>
      <div v-if="pickerKey === key" class="wa-picker" role="dialog" :aria-label="`${colorLabels[key]} colour`">
        <ColorPicker
          :ref="(c) => (picker = c as InstanceType<typeof ColorPicker> | null)"
          :modelValue="look[key]"
          @update:modelValue="look = { ...look, [key]: $event }"
        />
      </div>
    </div>
    <div class="wa-foot">
      <button class="wa-reset" type="button" @click="look = { ...WORKER_AVATAR_LOOK_DEFAULT }">Reset</button>
      <button class="wa-done" type="button" @click="emit('close')">Done</button>
    </div>
  </section>
</template>

<style scoped>
.wa-customizer {
  width: 372px;
  background: #081848;
  color: #fff;
  border: 1.5px solid #2d475e;
  border-radius: 18px;
  padding: 16px 20px;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.4);
  font-weight: 300;
}
.wa-customizer:focus {
  outline: none;
}
.wa-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 4px;
}
.wa-face {
  width: 56px;
  height: 56px;
  flex: none;
  display: block;
}
.wa-eyebrow {
  flex: 1;
  font-weight: 700;
  font-size: 15px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #ffc400;
}
.wa-close {
  background: none;
  border: 0;
  color: #92a5b9;
  font: inherit;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
}
.wa-close:hover {
  color: #fff;
}
.wa-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 9px;
}
.wa-label {
  width: 80px;
  flex: none;
  font-size: 14px;
  font-weight: 400;
  color: #92a5b9;
}
.wa-segments {
  display: flex;
  border: 1px solid #2d475e;
  border-radius: 999px;
  padding: 3px;
}
.wa-segments button {
  background: none;
  border: 0;
  color: #fff;
  font: inherit;
  font-size: 14px;
  font-weight: 400;
  padding: 6px 16px;
  border-radius: 999px;
  cursor: pointer;
}
.wa-segments button[aria-pressed='true'] {
  background: #fff;
  color: #081848;
  font-weight: 700;
}
.wa-swatches {
  display: flex;
  gap: 9px;
}
.wa-swatches button {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 0;
  padding: 0;
  cursor: pointer;
  box-shadow: inset 0 0 0 1.5px rgba(255, 255, 255, 0.3);
}
.wa-swatches button[aria-pressed='true'] {
  outline: 2px solid #fff;
  outline-offset: 2px;
}
.wa-custom {
  position: relative;
  background: conic-gradient(#f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00);
}
/* A custom colour sits inside the rainbow ring. */
.wa-custom span {
  position: absolute;
  inset: 5px;
  border-radius: 50%;
  box-shadow: 0 0 0 1.5px #081848;
}
.wa-picker {
  position: absolute;
  right: 0;
  top: calc(100% + 10px);
  z-index: 10;
  width: 240px;
  padding: 14px;
  background: #081848;
  border: 1.5px solid #2d475e;
  border-radius: 14px;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.45);
}
.wa-divider {
  width: 1px;
  align-self: stretch;
  background: #2d475e;
  margin: 2px 1px;
}
.wa-wear {
  background: none;
  border: 1px solid #2d475e;
  color: #fff;
  border-radius: 999px;
  padding: 6px 14px 6px 8px;
  font: inherit;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}
.wa-wear:hover {
  border-color: #92a5b9;
}
.wa-wear[aria-pressed='true'] {
  background: #fff;
  color: #081848;
  font-weight: 700;
  border-color: #fff;
}
.wa-wear i {
  display: flex;
}
.wa-wear i b {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: block;
  box-shadow: inset 0 0 0 1px rgba(8, 24, 72, 0.25);
}
.wa-wear i b + b {
  margin-left: -4px;
}
.wa-swatches button:focus-visible,
.wa-segments button:focus-visible,
.wa-close:focus-visible,
.wa-wear:focus-visible,
.wa-reset:focus-visible,
.wa-done:focus-visible {
  outline: 2px solid #ffc400;
  outline-offset: 2px;
}
.wa-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 14px;
}
.wa-reset {
  background: none;
  border: 0;
  color: #92a5b9;
  font: inherit;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.wa-reset:hover {
  color: #fff;
}
.wa-done {
  background: #fff;
  color: #081848;
  border: 0;
  border-radius: 999px;
  padding: 11px 18px;
  font: inherit;
  font-size: 15.5px;
  font-weight: 700;
  cursor: pointer;
}
</style>
